import angular from 'angular'
import findIndex from 'lodash/findIndex'

import AutocompleteController from '../vd-autocomplete.controller'
import KEY from '../../../constants/vd-key-codes'

const LOZENGE_ALREADY_ADDED = 'vd-lozenge-group-already-added'

/**
 * Controller for the vd-autocomplete-multi component.
 *
 * @class AutocompleteMultiController
 * @extends {AutocompleteController}
 */
export default class AutocompleteMultiController extends AutocompleteController {
  constructor(
    $q,
    $scope,
    $element,
    $timeout,
    $attrs,
    $log,
    $window,
    vdSuggestionPopover,
    vdTracking
  ) {
    'ngInject'
    super(
      $q,
      $scope,
      $element,
      $timeout,
      $window,
      $attrs,
      $log,
      vdSuggestionPopover,
      vdTracking
    )

    this._$window = $window

    this.data.selected = []
  }

  $onInit() {
    super.$onInit()

    if (this.maxItems && !this.maxPlaceholder) {
      this.maxPlaceholder = `You have reached the maximum number of items (${this.maxItems})`
    }

    if (this.ngModelCtrl) {
      this.ngModelCtrl.$isEmpty = value => {
        return !value || value.length === 0
      }

      // ng-model does not do a deep watch on the model value so would miss external changes to the model that can
      // only be detected by object equality (e.g. adding/removing something from an array)
      this._$scope.$watch(
        () => this.ngModelCtrl.$viewValue,
        () => this.ngModelCtrl.$render(),
        true
      )
    }
  }

  $postLink() {
    super.$postLink()

    this._$attrs.$addClass('vd-lozenge-group')

    // @note do not make any DOM tree changes here otherwise this will trigger an infinite loop
    const observer = new this._$window.MutationObserver(() =>
      this._positionTypeaheadHint()
    )
    observer.observe(this._$element[0], { childList: true, subtree: true })
  }

  getPlaceholder() {
    return this._reachedLimit() ? this.maxPlaceholder : this.placeholder
  }

  inputDisabled() {
    return this.disabled || this._reachedLimit()
  }

  onFocus() {
    this._$attrs.$addClass('vd-focus')
  }

  onBlur() {
    this._$attrs.$removeClass('vd-focus')
  }

  /**
   * Remove the selected item at the given index from the list of selected items.
   *
   * @method removeSelectedItem
   * @param  {Number} index
   */
  removeSelectedItem(index) {
    this.data.selected.splice(index, 1)
    this._updateModel()
  }

  // ============================== AutocompleteInputController overrides =============================== //

  /**
   * Indicates whether the maximum number of items has been entered.
   *
   * @method _reachedLimit
   * @private
   * @returns {boolean} true if the maximum number of items has been entered.
   */
  _reachedLimit() {
    return this.maxItems && this.data.selected.length >= this.maxItems
  }

  _select(suggestion) {
    const matchedIndex = findIndex(this.data.selected, {
      id: suggestion.id,
      type: suggestion.type,
    })

    if (matchedIndex > -1) {
      this._highlightAlreadySelectedItem(matchedIndex)
      return this._$q.reject()
    }

    this.data.selected.push(suggestion)
    this._updateModel()

    this._clearQuery()

    return this._$q.resolve(suggestion)
  }

  _initSearchInput() {
    super._initSearchInput()
    this._$element.on('click', () => this._searchInput[0].focus())
  }

  _handleKeyDown(key, event) {
    super._handleKeyDown(key, event)

    if (
      key === KEY.BACKSPACE &&
      !this.data.query &&
      this.data.selected.length
    ) {
      this.data.selected.pop()
      this._updateModel()
    }
  }

  _setIcon() {
    // Do nothing - icons can not be used with autocomplete-multi.
  }

  // ============================== AutocompleteController overrides =============================== //

  _updateModel() {
    super._updateModel([...this.data.selected])
  }

  _renderModel() {
    if (!angular.isArray(this.ngModelCtrl.$viewValue)) {
      this.ngModelCtrl.$viewValue = []
    }
    this.data.selected = this.ngModelCtrl.$viewValue
  }

  _clearModelOnQueryChange() {
    // Do nothing - we only care about updating the model when selected items are added/removed
  }

  // ============================== Private Methods =============================== //

  _highlightAlreadySelectedItem(index) {
    const addedLozenge = angular.element(
      Array.prototype.filter.call(this._$element[0].children, child =>
        child.classList.contains('vd-lozenge')
      )[index]
    )
    addedLozenge.addClass(LOZENGE_ALREADY_ADDED)
    addedLozenge.one('animationend', () => {
      addedLozenge.removeClass(LOZENGE_ALREADY_ADDED)
    })
  }

  _clearQuery() {
    this.data.query = ''
  }
}
